import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, Save, Sparkles, Sprout, Target, Trophy } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const GOAL_STATUSES = [
  { value: "on_track", label: "On Track" },
  { value: "at_risk", label: "At Risk" },
  { value: "off_track", label: "Off Track" },
  { value: "completed", label: "Completed" },
  { value: "paused", label: "Paused" },
];

const emptyGoal = {
  season_id: "", description: "", why_it_matters: "",
  success_measure: "", target_date: "", competency_id: "", linked_training_ids: [],
};

export default function GrowthPlan() {
  const { employeeId } = useParams();
  const { user } = useAuth();
  const [plan, setPlan] = useState(null);
  const [seasons, setSeasons] = useState([]);
  const [competencies, setCompetencies] = useState([]);
  const [settings, setSettings] = useState(null);
  const [growthFocus, setGrowthFocus] = useState("");
  const [growthFocusSaving, setGrowthFocusSaving] = useState(false);
  const [goal, setGoal] = useState(emptyGoal);
  const [win, setWin] = useState("");
  const [aiOpen, setAiOpen] = useState(null);
  const [aiInput, setAiInput] = useState("");
  const [aiResult, setAiResult] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  const load = async () => {
    try {
      const [p, s, c, st] = await Promise.all([
        api.get(`/rhythm/growth-plans/${employeeId}`),
        api.get(`/rhythm/seasons`),
        api.get(`/rhythm/competencies`),
        api.get(`/rhythm/settings`),
      ]);
      setPlan(p.data);
      setGrowthFocus(p.data.growth_focus || "");
      setSeasons(s.data);
      setCompetencies(c.data);
      setSettings(st.data);
      if (!goal.season_id && s.data.length) setGoal((g) => ({ ...g, season_id: s.data[0].id }));
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [employeeId]);

  const saveGrowthFocus = async (e) => {
    e?.preventDefault?.();
    setGrowthFocusSaving(true);
    try {
      await api.put(`/rhythm/growth-plans/${employeeId}/growth-focus`, {
        growth_focus: growthFocus || null,
      });
      toast.success("Growth Focus saved.");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setGrowthFocusSaving(false);
    }
  };

  const addGoal = async (e) => {
    e.preventDefault();
    if (!goal.description.trim() || !goal.season_id) return;
    try {
      const { data } = await api.post(`/rhythm/growth-plans/${employeeId}/goals`, goal);
      if (data.warn_over_three) {
        toast.warning("Heads up — that's more than 3 goals for this season. Consider consolidating.");
      } else {
        toast.success("Goal added.");
      }
      setGoal({ ...emptyGoal, season_id: goal.season_id });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
  };

  const updateGoalStatus = async (id, status) => {
    try {
      await api.put(`/rhythm/growth-plans/${employeeId}/goals/${id}`, { status });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
  };

  const addWin = async (e) => {
    e.preventDefault();
    if (!win.trim()) return;
    try {
      await api.post(`/rhythm/growth-plans/${employeeId}/wins`, { text: win });
      setWin("");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
  };

  const runAI = async () => {
    if (!aiInput.trim()) return;
    setAiLoading(true);
    try {
      const { data } = await api.post("/rhythm/ai/draft", {
        mode: aiOpen === "goal" ? "goal" : "win",
        input_text: aiInput,
      });
      setAiResult(data);
    } catch (err) { toast.error("AI unavailable right now."); }
    finally { setAiLoading(false); }
  };

  const useAI = () => {
    if (!aiResult) return;
    if (aiOpen === "goal" && aiResult.structured) {
      const s = aiResult.structured;
      setGoal((g) => ({
        ...g,
        description: s.description || g.description,
        why_it_matters: s.why_it_matters || g.why_it_matters,
        success_measure: s.success_measure || g.success_measure,
        target_date: s.target_date || g.target_date,
      }));
    }
    if (aiOpen === "win") setWin(aiResult.draft);
    setAiOpen(null); setAiInput(""); setAiResult(null);
  };

  if (!plan) return <p className="italic text-charcoal-muted p-6">Loading growth plan…</p>;

  return (
    <div className="space-y-6" data-testid="rhythm-growth-page">
      <div>
        <Link to="/rhythm" className="text-sm text-forest-700 hover:text-forest-900 inline-flex items-center gap-1">
          <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> Back To RHYTHM
        </Link>
        <p className="text-xs uppercase tracking-widest text-terracotta-500 font-medium mt-2">Growth Plan</p>
        <h1 className="text-4xl font-serif text-forest-900">Development Season</h1>
        <p className="mt-2 text-sm text-charcoal-soft italic max-w-2xl" data-testid="growth-disclaimer">
          {settings?.microcopy?.growth_plan_disclaimer ||
            "Development goals reflect shared intent, not a promise of promotion, advancement, or continued employment. Employment at Patria remains at-will."}
        </p>
      </div>

      {/* Growth Focus — the one required field. Everything below is optional. */}
      <section className="patria-card p-6 border-l-4 border-forest-600" data-testid="growth-focus-section">
        <div className="flex items-center gap-2">
          <Sprout className="h-5 w-5 text-forest-600" strokeWidth={1.5} />
          <h2 className="font-serif text-2xl text-forest-900">Growth Focus</h2>
        </div>
        <p className="text-xs text-charcoal-muted mt-1">
          One or two sentences describing where you want to grow this season.
          This is the only required part of your Growth Plan — goals and
          competencies below are optional if focus is enough.
        </p>
        <form onSubmit={saveGrowthFocus} className="mt-4 space-y-3">
          <textarea
            className="patria-input w-full min-h-[100px]"
            placeholder="This season I want to grow in… (e.g., leading meetings with more clarity; delegating better; deepening my discipleship of new leaders.)"
            value={growthFocus}
            onChange={(e) => setGrowthFocus(e.target.value)}
            maxLength={1000}
            data-testid="growth-focus-input"
          />
          <div className="flex items-center gap-3 flex-wrap">
            <button
              type="submit"
              disabled={growthFocusSaving}
              className="patria-btn-primary min-h-[44px]"
              data-testid="btn-save-growth-focus"
            >
              <Save className="h-4 w-4 mr-1.5" strokeWidth={1.5} />
              {growthFocusSaving ? "Saving…" : "Save Focus"}
            </button>
            {plan.growth_focus_updated_at && (
              <span className="text-xs text-charcoal-muted italic">
                Last updated {plan.growth_focus_updated_at.slice(0, 10)}
              </span>
            )}
          </div>
        </form>
      </section>

      {/* Goals */}
      <section className="patria-card p-6" data-testid="growth-goals-section">
        <div className="flex items-center gap-2">
          <Target className="h-5 w-5 text-forest-900" strokeWidth={1.5} />
          <h2 className="font-serif text-2xl text-forest-900">Goals</h2>
          <span className="text-[10px] uppercase tracking-widest text-charcoal-muted ml-2">Optional</span>
        </div>
        <p className="text-xs text-charcoal-muted mt-1">
          Add up to 3 SMART goals if concrete milestones help your focus.
          Competencies are optional — skip them if they don&rsquo;t fit the shape of your goal.
        </p>
        <ul className="mt-4 space-y-3">
          {(plan.goals || []).map((g) => (
            <li key={g.id} className="p-4 rounded-xl bg-cream-100" data-testid={`goal-row-${g.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-medium text-forest-900">{g.description}</p>
                  {g.why_it_matters && <p className="text-xs text-charcoal-muted mt-1">Why it matters: {g.why_it_matters}</p>}
                  {g.success_measure && <p className="text-xs text-charcoal-muted">Success measure: {g.success_measure}</p>}
                  <p className="text-[10px] uppercase tracking-widest text-charcoal-muted mt-2">
                    Season: {seasons.find((s) => s.id === g.season_id)?.name || "—"} · Target: {g.target_date || "—"}
                  </p>
                </div>
                <select
                  className="patria-input text-sm"
                  value={g.status}
                  onChange={(e) => updateGoalStatus(g.id, e.target.value)}
                  data-testid={`goal-status-${g.id}`}
                >
                  {GOAL_STATUSES.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>
            </li>
          ))}
          {plan.goals.length === 0 && <li className="text-sm text-charcoal-muted italic">No goals yet.</li>}
        </ul>

        <form onSubmit={addGoal} className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <input
            className="patria-input sm:col-span-2"
            placeholder="Describe the goal…"
            value={goal.description}
            onChange={(e) => setGoal({ ...goal, description: e.target.value })}
            data-testid="new-goal-description"
            required
          />
          <input
            className="patria-input"
            placeholder="Why it matters"
            value={goal.why_it_matters}
            onChange={(e) => setGoal({ ...goal, why_it_matters: e.target.value })}
            data-testid="new-goal-why"
          />
          <input
            className="patria-input"
            placeholder="Success measure"
            value={goal.success_measure}
            onChange={(e) => setGoal({ ...goal, success_measure: e.target.value })}
            data-testid="new-goal-measure"
          />
          <select
            className="patria-input"
            value={goal.season_id}
            onChange={(e) => setGoal({ ...goal, season_id: e.target.value })}
            data-testid="new-goal-season"
          >
            {seasons.map((s) => (<option key={s.id} value={s.id}>{s.name}</option>))}
          </select>
          <select
            className="patria-input"
            value={goal.competency_id}
            onChange={(e) => setGoal({ ...goal, competency_id: e.target.value })}
            data-testid="new-goal-competency"
          >
            <option value="">Competency (optional)</option>
            {competencies.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
          </select>
          <input
            type="date"
            className="patria-input"
            value={goal.target_date}
            onChange={(e) => setGoal({ ...goal, target_date: e.target.value })}
            data-testid="new-goal-target"
          />
          <div className="sm:col-span-2 flex items-center gap-2">
            <button type="button" className="patria-btn-ghost text-sm" onClick={() => { setAiOpen("goal"); setAiInput(""); setAiResult(null); }} data-testid="btn-ai-goal">
              <Sparkles className="h-3.5 w-3.5" strokeWidth={1.5} /> Draft With AI
            </button>
            <button type="submit" className="patria-btn-primary" data-testid="btn-add-goal">
              <Plus className="h-4 w-4 mr-1" strokeWidth={1.5} /> Add Goal
            </button>
          </div>
        </form>
      </section>

      {/* Wins log */}
      <section className="patria-card p-6" data-testid="growth-wins-section">
        <div className="flex items-center gap-2">
          <Trophy className="h-5 w-5 text-terracotta-500" strokeWidth={1.5} />
          <h2 className="font-serif text-2xl text-forest-900">Wins Log</h2>
        </div>
        <form onSubmit={addWin} className="mt-4 flex gap-2">
          <input
            className="patria-input flex-1"
            placeholder="A dated win…"
            value={win}
            onChange={(e) => setWin(e.target.value)}
            data-testid="new-win-text"
          />
          <button type="button" className="patria-btn-ghost" onClick={() => { setAiOpen("win"); setAiInput(""); setAiResult(null); }} data-testid="btn-ai-win">
            <Sparkles className="h-4 w-4" strokeWidth={1.5} />
          </button>
          <button type="submit" className="patria-btn-primary" data-testid="btn-add-win">
            <Plus className="h-4 w-4 mr-1" strokeWidth={1.5} /> Log Win
          </button>
        </form>
        <ul className="mt-4 space-y-2">
          {(plan.wins || []).map((w) => (
            <li key={w.id} className="p-3 rounded-xl bg-cream-100" data-testid={`win-row-${w.id}`}>
              <p className="text-forest-900">{w.text}</p>
              <p className="text-[10px] uppercase tracking-widest text-charcoal-muted mt-1">
                {w.dated_on} · logged by {w.by_name}
              </p>
            </li>
          ))}
          {plan.wins.length === 0 && <li className="text-sm text-charcoal-muted italic">No wins logged yet.</li>}
        </ul>
      </section>

      {/* AI modal */}
      {aiOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="growth-ai-modal">
          <div className="absolute inset-0 bg-forest-900/40 backdrop-blur-sm" onClick={() => setAiOpen(null)} />
          <div className="relative w-full max-w-lg mx-4 bg-cream-100 rounded-2xl shadow-card p-6">
            <h3 className="font-serif text-2xl text-forest-900">Draft With AI</h3>
            <p className="text-sm text-charcoal-soft mt-1">
              Rough fragments become structured {aiOpen === "goal" ? "goals" : "win entries"}. The draft never adds facts.
            </p>
            <textarea
              className="patria-input w-full mt-3 min-h-[100px]"
              value={aiInput}
              onChange={(e) => setAiInput(e.target.value)}
              placeholder={aiOpen === "goal" ? "- build volunteer onboarding rhythm\n- measure completion in 30 days" : "- Ran Sunday service on time"}
              data-testid="growth-ai-input"
            />
            <button onClick={runAI} disabled={aiLoading || !aiInput.trim()} className="patria-btn-primary mt-3" data-testid="btn-growth-ai-run">
              {aiLoading ? "Drafting…" : "Draft"}
            </button>
            {aiResult && (
              <div className="mt-3 p-3 rounded-xl bg-sage-200/40" data-testid="growth-ai-result">
                {aiResult.stripped_pastoral && (
                  <div className="text-xs text-terracotta-700 mb-2">{aiResult.two_track_notice}</div>
                )}
                <pre className="whitespace-pre-wrap text-sm text-forest-900">{aiResult.draft}</pre>
                <button onClick={useAI} className="patria-btn-primary mt-3" data-testid="btn-growth-ai-use">
                  Use This Draft
                </button>
              </div>
            )}
            <button onClick={() => setAiOpen(null)} className="patria-btn-ghost mt-3">Close</button>
          </div>
        </div>
      )}
    </div>
  );
}
